import React from "react";
import { AlertTriangle, Clock, Users, Timer, TrendingDown } from "lucide-react";
import { fmtDuration } from "@/lib/fmtDuration";

const pct = (x) => `${Math.round((Number(x) || 0) * 100)}%`;

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border hairline bg-white p-4" data-testid={testid}>
      <p className="font-mono-brand text-[9px] tracking-[0.12em] uppercase text-propertic-muted flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </p>
      <p className="font-editorial text-2xl text-propertic-navy tabular-nums mt-1" data-testid={`${testid}-value`}>
        {value}
      </p>
      {sub && <p className="text-[10px] text-propertic-muted mt-0.5">{sub}</p>}
    </div>
  );
}

function LowEngagementAlert({ e }) {
  return (
    <div
      className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4"
      data-testid="low-engagement-alert"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-amber-900">
            Low engagement: {pct(e.low_engagement_share)} of sessions ({e.low_engagement_sessions} of {e.sessions_total}) stayed
            under {e.low_engagement_threshold_s}s with no clicks. Alert threshold is {pct(e.alert_threshold_share)}.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Breakdown title="Top sources" rows={e.low_top_sources} label={(r) => r.source} testid="low-eng-source" />
            <Breakdown title="Top landing pages" rows={e.low_top_pages} label={(r) => r.path} testid="low-eng-page" mono />
          </div>
        </div>
      </div>
    </div>
  );
}

function Breakdown({ title, rows, label, testid, mono }) {
  return (
    <div>
      <p className="font-mono-brand text-[9px] tracking-[0.12em] uppercase text-amber-800 mb-1">{title}</p>
      <ul className="space-y-0.5">
        {(rows || []).map((r, i) => (
          <li key={i} className="flex justify-between gap-3 text-[11px] text-amber-900" data-testid={`${testid}-${i}`}>
            <span className={`truncate ${mono ? "font-mono-brand" : ""}`} title={label(r)}>{label(r)}</span>
            <span className="tabular-nums flex-shrink-0">{r.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function EngagementKpis({ engagement: e }) {
  if (!e) return null;
  return (
    <>
      {e.alert && <LowEngagementAlert e={e} />}
      <div className="mb-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="engagement-kpis">
        <Kpi icon={Clock} label="Avg session duration" value={fmtDuration(e.avg_session_s)}
          sub={`${e.engaged_sessions} engaged sessions`} testid="kpi-avg-session" />
        <Kpi icon={Timer} label="Median session" value={fmtDuration(e.median_session_s)} testid="kpi-median-session" />
        <Kpi icon={Users} label="Total sessions" value={(e.sessions_total || 0).toLocaleString()} testid="kpi-total-sessions" />
        <Kpi icon={TrendingDown} label="Low engagement" value={pct(e.low_engagement_share)}
          sub={`<${e.low_engagement_threshold_s}s & 0 clicks`} testid="kpi-low-engagement" />
      </div>
    </>
  );
}
